import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { trackEvent } from '@/utils/analytics'
import { TOUR_OVERLAY_Z } from '../constants'
import { getTour } from './tourRegistry'
import TourTooltip from './TourTooltip'
import { useTargetRect } from './useTargetRect'

const DEFAULT_SPOTLIGHT_PADDING = 8

/**
 * Guided-tour renderer: a four-rect dim overlay with a REAL hole over the
 * target (so it stays clickable while the rects swallow outside clicks),
 * a ring outline, and the dark coach-mark tooltip.
 *
 * Steps that declare a `route` navigate there first; the target poller
 * tolerates the transition. A target that never appears (flag-gated UI,
 * removed element) skips the step — or ends the tour with a toast when
 * it was the last one.
 */
const TourOverlay = () => {
  const { t } = useTranslation('help_agent')
  const helpActiveTour = useAppStore((s) => s.helpActiveTour)
  const advanceHelpTour = useAppStore((s) => s.advanceHelpTour)
  const endHelpTour = useAppStore((s) => s.endHelpTour)
  const location = useLocation()
  const navigate = useNavigate()

  const tour = helpActiveTour ? getTour(helpActiveTour.tourId) : undefined
  const step = tour?.steps[helpActiveTour?.stepIndex ?? 0]

  const trackedTourRef = useRef<string | null>(null)
  useEffect(() => {
    // Clear the ref when no tour is active so re-launching the SAME tour in
    // one session fires tour_started again (greptile #18060).
    if (!tour) {
      trackedTourRef.current = null
      return
    }
    if (trackedTourRef.current !== tour.id) {
      trackedTourRef.current = tour.id
      trackEvent('help_agent-tour_started', { tour_id: tour.id })
    }
  }, [tour])

  // Bring the user to the step's route before hunting for its target.
  useEffect(() => {
    if (step?.route && step.route !== location.pathname) {
      navigate(step.route)
    }
  }, [step, location.pathname, navigate])

  const { rect, status, targetId: statusTargetId } = useTargetRect(step?.targetId ?? '')

  useEffect(() => {
    // statusTargetId guard: state updates lag one render behind a step
    // change, so right after a skip the previous step's 'missing' is still
    // in flight — acting on it would cascade-skip healthy steps.
    if (status !== 'missing' || !step || statusTargetId !== step.targetId) return
    toast.error(t('tour.target_missing'))
    advanceHelpTour() // skips to the next step; ends the tour on the last
  }, [status, statusTargetId, step, advanceHelpTour, t])

  if (!tour || !step || !helpActiveTour) return null

  const stepNumber = helpActiveTour.stepIndex + 1
  const stepCount = tour.steps.length

  const onNext = (): void => {
    if (stepNumber === stepCount) {
      trackEvent('help_agent-tour_completed', { tour_id: tour.id })
    }
    advanceHelpTour()
  }
  const onSkip = (): void => {
    trackEvent('help_agent-tour_skipped', { tour_id: tour.id, step: stepNumber })
    endHelpTour()
  }

  // While searching, dim the whole screen with no hole — signals "tour
  // starting" through route transitions without flashing.
  if (!rect) {
    return (
      <div className={`fixed inset-0 ${TOUR_OVERLAY_Z} bg-black/60`} data-testid="tour-dim" />
    )
  }

  const padding = step.spotlightPadding ?? DEFAULT_SPOTLIGHT_PADDING
  const hole = {
    top: Math.max(rect.top - padding, 0),
    left: Math.max(rect.left - padding, 0),
    right: Math.min(rect.right + padding, window.innerWidth),
    bottom: Math.min(rect.bottom + padding, window.innerHeight),
  }

  return (
    <div className={`fixed inset-0 ${TOUR_OVERLAY_Z} pointer-events-none`} data-testid="tour-overlay">
      {/* Four dim rects around the hole — pointer-events-auto so clicks
          outside the target are swallowed; the hole itself stays live. */}
      <div
        className="pointer-events-auto absolute inset-x-0 top-0 bg-black/60"
        style={{ height: hole.top }}
      />
      <div
        className="pointer-events-auto absolute inset-x-0 bottom-0 bg-black/60"
        style={{ top: hole.bottom }}
      />
      <div
        className="pointer-events-auto absolute bg-black/60"
        style={{ top: hole.top, height: hole.bottom - hole.top, left: 0, width: hole.left }}
      />
      <div
        className="pointer-events-auto absolute bg-black/60"
        style={{ top: hole.top, height: hole.bottom - hole.top, left: hole.right, right: 0 }}
      />
      <div
        className="pointer-events-none absolute rounded-lg ring-2 ring-white/80"
        style={{
          top: hole.top,
          left: hole.left,
          width: hole.right - hole.left,
          height: hole.bottom - hole.top,
        }}
      />
      {/* `contents` passes pointer-events:auto through the events-none
          overlay so the tooltip stays clickable while clicks elsewhere are
          swallowed by the dim rects. */}
      <div className="pointer-events-auto contents">
        <TourTooltip
          step={step}
          targetRect={rect}
          stepNumber={stepNumber}
          stepCount={stepCount}
          onNext={onNext}
          onSkip={onSkip}
        />
      </div>
    </div>
  )
}

export default TourOverlay
